import { useBoolean } from "@/hooks/use-boolean";
import { useViewerHasAtLeastAdminRights } from "@/lib/rights/use-viewer-has-right";
import useTranslation from "@babackapp/translations/use-translation";
import { Alert, AlertDescription } from "@babackapp/ui/components/alert";
import { Button } from "@babackapp/ui/components/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@babackapp/ui/components/card";
import { AlertTriangle } from "lucide-react";
import { useMemo, useState } from "react";
import { useCarriersContext } from "../../use-carriers-context";
import { AddCarrierTransportDialog } from "../forms/add-carrier-transport-dialog";
import { DeleteCarrierTransportDialog } from "../forms/delete-carrier-transport-dialog";
import { EditCarrierTransportDialog } from "../forms/edit-carrier-transport-dialog";
import { extractCarrierTagValue } from "../lib/tag-conditions.helpers";
import { CarrierListItem } from "./carrier-list-item";

export const CarriersPricings = () => {
  const { shop } = useCarriersContext();
  const { carriers } = useCarriersContext();
  // Track the id, not a carrier snapshot, so the edit modal reflects the live
  // carrier after preference saves refetch (FedexPlanSelect auto-saves).
  const [selectedTransportId, setSelectedTransportId] = useState<
    string | undefined
  >(undefined);
  const selectedTransport = useMemo(
    () => carriers.find((c) => c.id === selectedTransportId),
    [carriers, selectedTransportId],
  );
  const hasAdminRights = useViewerHasAtLeastAdminRights();
  const editTransportationModale = useBoolean(false);
  const onCloseEditingTransportation = useMemo(() => {
    return () => {
      editTransportationModale.setFalse();
      setSelectedTransportId(undefined);
    };
  }, []);

  const onEditCarrierTransport = (selectedTransportID: string | undefined) => {
    setSelectedTransportId(selectedTransportID);
    editTransportationModale.setTrue();
  };

  const deleteTransportationModale = useBoolean(false);
  const onCloseDeleteTransportation = useMemo(() => {
    return () => {
      deleteTransportationModale.setFalse();
      setSelectedTransportId(undefined);
    };
  }, []);
  const onDeleteCarrierTransport = (
    selectedTransportID: string | undefined,
  ) => {
    setSelectedTransportId(selectedTransportID);
    deleteTransportationModale.setTrue();
  };

  const t = useTranslation();

  const allCarriersHaveConditionalTags = useMemo(() => {
    if (carriers.length === 0) return false;

    return carriers.every((carrier) => {
      const tagValue = extractCarrierTagValue(carrier.conditionalRules);
      return tagValue !== null;
    });
  }, [carriers]);

  if (!shop) {
    return <></>;
  }

  return (
    <>
      {selectedTransport && editTransportationModale.value && (
        <EditCarrierTransportDialog
          selectedCarrier={selectedTransport}
          onClose={onCloseEditingTransportation}
          setIsModalOpen={editTransportationModale.setValue}
          isOpen={editTransportationModale.value}
        />
      )}
      {selectedTransport && deleteTransportationModale.value && (
        <DeleteCarrierTransportDialog
          selectedCarrierID={selectedTransport?.id}
          closeForm={onCloseDeleteTransportation}
          setIsModalOpen={deleteTransportationModale.setValue}
          isOpen={deleteTransportationModale.value}
        />
      )}
      <Card>
        <CardHeader>
          <CardTitle> {t("settings.carriers.title")}</CardTitle>
          <CardDescription>
            <div className="flex flex-row justify-between">
              <div>
                {t("carrier.carrierSubBtnLabel", {
                  count: carriers.length,
                })}
              </div>
              <div>
                {shop &&
                  hasAdminRights &&
                  !shop.plan.transportManagedByBB.activated && (
                    <AddCarrierTransportDialog>
                      <Button variant="default" size="sm">
                        {t("carrier.addCarrier")}
                      </Button>
                    </AddCarrierTransportDialog>
                  )}
              </div>
            </div>
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-6">
          {allCarriersHaveConditionalTags && (
            <Alert className="mb-4 border-yellow-200 bg-yellow-50">
              <AlertTriangle className="h-4 w-4 text-yellow-600" />
              <AlertDescription className="text-yellow-800">
                {t("carrier.tagCondition.warning")}
              </AlertDescription>
            </Alert>
          )}
          {!shop.plan.transportManagedByBB.activated && carriers.length > 0 && (
            <div className="space-y-6 pt-6 ">
              {carriers.map((carrier) => (
                <CarrierListItem
                  key={carrier.id}
                  carrier={carrier}
                  shopId={shop.id}
                  onEdit={onEditCarrierTransport}
                  onDelete={onDeleteCarrierTransport}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </>
  );
};
